import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import Seo from '../components/Seo';
import CategoryHeader from '../components/CategoryHeader';
import CategoryIcon from '../components/CategoryIcon';
import categoryImageSrc from '../utils/categoryImageSrc';
import { CATEGORY_SEO_MAP } from '../utils/categorySeoData';
import cleanSubcategoryName from '../utils/cleanSubcategoryName';
import { generateCollectionPageSchema } from '../utils/schemaGenerators';
import '../styles/pages/CategoryPage.scss';

const API_URL = process.env.REACT_APP_API_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);

  // 1) Fetch de categorías (+ subcategorías) al montar
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/categories`, {
          headers: { authorization: token },
        });
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories', error);
      }
    };

    fetchCategories();
  }, []);

  // 2) Agrupar en filas de 4 categorías cada una
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };
  const categoryChunks = chunkArray(categories, 4);

  const pageTitle = 'Categorías – Promocionales en Stock – Catálogo de productos promocionales personalizados en Colombia.';
  const pageDescription =
    'Explora nuestra amplia selección de productos promocionales diseñados para satisfacer tus necesidades en cada aspecto de tu vida. Desde artículos para el hogar y herramientas prácticas, hasta tecnología innovadora y soluciones de cuidado personal.';
  const canonicalUrl = '/categorias';
  const breadcrumbs = [
    { name: 'Inicio', url: '/' },
    { name: 'Categorías', url: '/categorias' },
  ];
  const collectionJsonLd = generateCollectionPageSchema({
    title: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    breadcrumbs,
    currentPage: 1,
    totalPages: 1,
  });
  const additionalMetaTags = [
    { name: 'robots', content: 'index,follow' },
    {
      name: 'keywords',
      content:
        'categorías promocionales, productos promocionales, merchandising corporativo, regalos empresariales',
    },
    { name: 'author', content: 'Promocionales en Stock' },
  ];

  return (
    <div className="CategoriesPage container">
      {/* == SEO y JSON-LD == */}
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        prevPageUrl={null}
        nextPageUrl={null}
        ogImage="/images/og-default.jpg"
        ogType="website"
        jsonLd={collectionJsonLd}
        additionalMeta={additionalMetaTags}
        siteUrl={SITE_URL}
      />

      {/* == Encabezado visual == */}
      <CategoryHeader title="Categorías" subtitle={pageDescription} />

      {/* == Render de categorías en cuadrícula 4xN == */}
      {categoryChunks.map((chunk, rowIndex) => (
        <div key={rowIndex} className="categories-row">
          <div className="row">
            {chunk.map((category) => (
              <div key={category.id} className="category-item col-12 col-md-3">
                <CategoryIcon slug={category.slug} imageUrl={categoryImageSrc(category)} className="category-icon" />
                <div className="categories-list-links">
                  <h2>{CATEGORY_SEO_MAP[category.slug]?.name || category.name}</h2>
                  <ul>
                    {[...category.subcategories].sort((a, b) => cleanSubcategoryName(a.name).localeCompare(cleanSubcategoryName(b.name), 'es')).map((subcategory) => (
                      <li key={subcategory.id}>
                        <Link
                          to={`/categorias/${category.slug}/${subcategory.slug}`}
                          className={
                            subcategory.name.toLowerCase() === 'todos'
                              ? 'subcategory-todos'
                              : ''
                          }
                          aria-label={`Ver detalles de ${cleanSubcategoryName(subcategory.name)}`}
                        >
                          {cleanSubcategoryName(subcategory.name)}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link 
                      to={`/categorias/${category.slug}`}
                      aria-label={`Ver detalles de ${category.name}`}>Todos</Link>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CategoriesPage;
